import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Box, Autocomplete, TextField, Button, FormControlLabel, Checkbox, Dialog, DialogTitle, DialogContent, Typography, DialogActions } from '@mui/material';
import { useDataProvider, useGetIdentity, useGetList, useListContext, useRefresh } from 'react-admin';
import SearchIcon from '@mui/icons-material/Search';
import { DEFAULTS } from '../constants';

const BillingClientsFtpsFilters = () => {
  const { setFilters, filterValues, sort, setSort } = useListContext();
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [inputValue, setInputValue] = useState('');
  // For the "Show only clients without an FTP folder" toggle
  const [showClientsWithoutFtp, setShowClientsWithoutFtp] = useState(false);
  // For "Add New Partial Client" functionality
  const openDialogButtonRef = useRef<HTMLButtonElement | null>(null);
  const [formValues, setFormValues] = useState<any>({});
  const [showAddNewPartialClientDialog, setShowAddNewPartialClientDialog] = useState(false);
  const [dialogErrorText, setDialogErrorText] = useState('');
  const dataProvider = useDataProvider();
  const { identity } = useGetIdentity();
  const refresh = useRefresh();

  // Fetching the global client list for the autocomplete dropdown
  const { data: clientChoices = [], isLoading } = useGetList('clients', {
    pagination: { page: 1, perPage: 5000 },
    sort: { field: 'client_name', order: 'ASC' },
  });

  // Set default sorting on component mount
  useEffect(() => {
    setSort({ field: 'billing_client_number', order: 'ASC' });
  }, []);

  // Fetching list based on filters to determine if selected client has an FTP folder configuration
  const { data: ftpData = [], refetch } = useGetList('BillingClientsFtps', {
    pagination: { page: 1, perPage: 5000 },
    filter: { billing_client_number: filterValues.billing_client_number },
    sort
  });

  const filteredOptions = useMemo(
    () =>
      clientChoices.filter(
        (c: any) =>
          (c.client_name ?? '').toLowerCase().includes(inputValue.toLowerCase()) ||
          (c.client_number ?? '').toLowerCase().includes(inputValue.toLowerCase())
      ),
    [clientChoices, inputValue]
  );

  const handleApply = () => {
    setFilters({
      ...filterValues,
      // We pass billing_client_number to the backend as defined in our schema
      billing_client_number: selectedClient?.client_number || undefined,
    });
  };

  const handleClear = () => {
    setSelectedClient(null);
    setInputValue('');
    setFilters({});
    setShowClientsWithoutFtp(false);
  };

  const handleToggleShowClientsWithoutFtp = (checked: boolean) => {
    setShowClientsWithoutFtp(checked);
    if (checked) {
      setFilters({ ...filterValues, ftp_folder_name: '__EMPTY__' });
    } else {
      const { ftp_folder_name, ...withoutFtpFolders } = filterValues;
      setFilters(withoutFtpFolders);
    }
  };

  const handleAddNewPartialClientButton = () => {
    setShowAddNewPartialClientDialog(true);
  };

  const handleClosingAddNewPartialClientDialog = () => {
    setShowAddNewPartialClientDialog(false);
    setFormValues({});
    setDialogErrorText('');
  };

  const handleSaveNewPartialClient = async () => {
    // Save the new partial client configuration
    // Check for the following errors:
    // - Combination of data doesn't already exist to prevent duplicates
    // After saving, refresh the list and close the dialog
    const combinationExists = ftpData.some((record) => 
      record.billing_client_number === selectedClient.client_number &&
      record.partial_client_name.toLowerCase() === formValues.partial_client_name.toLowerCase() &&
      record.ftp_folder_name.toLowerCase() === formValues.ftp_folder_name.toLowerCase());
    if (combinationExists) {
      setDialogErrorText('A configuration with this Billing Client, Partial Client Name, and FTP Folder Name already exists');
      return;
    }

    // Call API to save new configuration
    await dataProvider.create('BillingClientsFtps', {
      data: {
        billing_client_number: selectedClient.client_number,
        billing_client_name: selectedClient.client_name,
        partial_client_name: formValues.partial_client_name,
        ftp_folder_name: formValues.ftp_folder_name,
        created_by: identity?.fullName || DEFAULTS.UNKNOWN_USER,
      },
    });
    setShowAddNewPartialClientDialog(false);
    setFormValues({});
    setDialogErrorText('');
    refresh(); // Trigger a refresh with to show the new entry
  }

  return (
    <>
      <Box>
        <Box display="flex" gap={3} px={2} py={2} alignItems="center" paddingBottom={0}>
          <Autocomplete
            options={filteredOptions}
            value={selectedClient}
            onChange={(e, value) => setSelectedClient(value)}
            inputValue={inputValue}
            onInputChange={(e, value) => setInputValue(value)}
            getOptionLabel={(option) => `${option.client_name} (${option.client_number})`}
            isOptionEqualToValue={(opt, val) => opt.client_number === val.client_number}
            loading={isLoading}
            sx={{ width: 400 }}
            renderInput={(params) => <TextField {...params} label="Client Name" variant="standard" size="small" />}
            filterOptions={(x) => x}
          />

          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleApply}
            sx={{
              minWidth: 100,
              height: 40,
              px: 2,
              borderRadius: 2,
              fontSize: '0.85rem',
              fontWeight: 500,
              textTransform: 'none',
            }}
          >
            Apply
          </Button>

          <Button
            variant="outlined"
            onClick={handleClear}
            sx={{
              minWidth: 100,
              height: 40,
              px: 2,
              borderRadius: 2,
              fontSize: '0.85rem',
              fontWeight: 500,
              textTransform: 'none',
            }}
          >
            Clear
          </Button>

          <Button
            variant="contained"
            ref={openDialogButtonRef}
            onClick={handleAddNewPartialClientButton}
            disabled={!(selectedClient && Object.keys(filterValues).length > 0)}
            sx={{
              minWidth: 100,
              height: 40,
              px: 2,
              borderRadius: 2,
              fontSize: '0.85rem',
              fontWeight: 500,
              textTransform: 'none',
            }}
          >
            Add New Partial Client
          </Button>
        </Box>
        <Box display="flex" px={2} alignItems="center">
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showClientsWithoutFtp}
                onChange={(e) => handleToggleShowClientsWithoutFtp(e.target.checked)}
              />
            }
            label="Show only clients without an FTP folder"
            sx={{ whiteSpace: 'nowrap' }}
          />
        </Box>
      </Box>
      <Dialog 
        open={showAddNewPartialClientDialog}
        onClose={handleClosingAddNewPartialClientDialog}
        TransitionProps={{
          onExited: () => {
            openDialogButtonRef.current?.focus();
          },
        }}
        PaperProps={{
          sx: {
            width: '50vw', // 50% of browser width
          },
        }}
      >
        <DialogTitle align='center'>Add New Partial Client</DialogTitle>
        <DialogContent>
          <Typography>
            Billing Client Number: <strong>{selectedClient?.client_number}</strong>
            <br />
            Billing Client Name: <strong>{selectedClient?.client_name}</strong>
          </Typography>
          <TextField
            label="Partial Client Name*"
            value={formValues.partial_client_name || ''}
            onChange={(e) => {
              setFormValues({ ...formValues, partial_client_name: e.target.value })
              setDialogErrorText('');
            }}
          />
          <TextField
            label="FTP Folder Name"
            value={formValues.ftp_folder_name || ''}
            onChange={(e) => {
              setFormValues({ ...formValues, ftp_folder_name: e.target.value })
              setDialogErrorText('');

            }}
          />
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: '4fr 1fr',
            gap: 2,
            alignItems: 'center',
          }}>
            {dialogErrorText && (
              <Typography
                color="error"
              >
                {dialogErrorText}
              </Typography>
            )}
            <DialogActions sx={{ paddingTop: 2, justifyContent: 'flex-end', gridColumn: '2' }}>
              <Button
                color="primary"
                variant="outlined"
                onClick={handleClosingAddNewPartialClientDialog}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={handleSaveNewPartialClient}
                disabled={!(formValues.partial_client_name)}
              >
                Save
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>
    </>      
  );
};

export default BillingClientsFtpsFilters;
